import { selectTechniques } from "domain/techniques/techniques.selectors";
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { TechniqueCard } from "ui/pages/app/components/activities/techniques/TechniqueCard";
import { DesktopActivitiesFilters } from "ui/pages/app/components/activities/DesktopActivitiesFilters";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { useWindowWidth } from "ui/utils/hooks";
import { HiAdjustments, HiSparkles } from "react-icons/hi";
import { INSkeleton } from "ui/pages/app/components/inputs/INSkeleton";
import {
  DurationFilter,
  ActivitiesPageFilters,
} from "domain/entities/activities.entities";
import {
  getTechniques,
  setIsTechniqueLoading,
} from "domain/techniques/techniques.actions";
import { MobileActivitiesFilters } from "ui/pages/app/components/activities/MobileActivitiesFilters";
import { useNavigate } from "react-router-dom";
import { getFiltersCount } from "ui/utils/get-filters-count";
import { INSearchField } from "ui/pages/app/components/inputs/INSearchField";
import { Technique } from "domain/entities/techniques.entities";
import { Snackbar } from "ui/pages/snackbar";
import { selectIsPremium, selectUserId } from "domain/auth/auth.selectors";
import { CustomTooltip } from "ui/pages/app/components/CustomTooltip";
import { AISearchTooltip } from "ui/pages/app/components/activities/AI/AISearchTooltip";
import { selectOnBoardingValues } from "domain/on-boarding/on-boarding.selectors";
import { updateOnBoardingValue } from "ui/utils/on-boarding-storage";
import { AISearchButton } from "ui/pages/app/components/activities/AI/AISearchButton";
import { useDisclosure } from "@chakra-ui/hooks";
import { ModalAISearch } from "ui/pages/app/components/activities/AI/ModalAISearch";
import { AIFeedbackToast } from "ui/pages/app/components/activities/AI/feedbacks/AIFeedbackToast";
import {
  selectIsAIFeedbackToastOpen,
  selectIsSearchingTechnique,
  selectTechniquesFromAISearch,
} from "domain/ai-search/ai-search.selectors";

export function Techniques({
  filters,
  setFilters,
  minDuration,
  maxDuration,
  flippedCards,
  setFlippedCards,
}: {
  filters: ActivitiesPageFilters & {
    [key: string]: string[] | string | boolean | number | DurationFilter;
  };
  setFilters: Dispatch<
    SetStateAction<
      ActivitiesPageFilters & {
        [key: string]: string | number | boolean | DurationFilter | string[];
      }
    >
  >;
  minDuration: number;
  maxDuration: number;
  flippedCards: number[];
  setFlippedCards: Dispatch<SetStateAction<number[]>>;
}) {
  const dispatch = useAppDispatch();
  const isPremium = useAppSelector(selectIsPremium);
  const aiTechniqueRefs = useAppSelector(selectTechniquesFromAISearch);
  const techniques = useAppSelector(selectTechniques)
    .filter((technique) =>
      aiTechniqueRefs.length === 0
        ? true
        : aiTechniqueRefs.includes(technique.innizRef)
    )
    //sort techniques based on AI search order
    .sort((a, b) => {
      if (aiTechniqueRefs.length === 0) return 0;
      return (
        aiTechniqueRefs.indexOf(a.innizRef) -
        aiTechniqueRefs.indexOf(b.innizRef)
      );
    });
  const isAIFeedbackToastOpen = useAppSelector(selectIsAIFeedbackToastOpen);
  const isSearching = useAppSelector(selectIsSearchingTechnique);
  const navigate = useNavigate();
  const isDesktop = useWindowWidth();
  const [search, setSearch] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const modalAISearch = useDisclosure();

  const fetchTechniques = async () => {
    setIsLoading(true);
    await dispatch(
      getTechniques({
        pauses: false,
        search,
        isInniz: true,
        ...filters,
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchTechniques();
    }, 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [filters, search]); //eslint-disable-line

  const handleCardClick = async (technique: Technique) => {
    if (!isPremium && !!technique.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(() => navigate("/account"), "technique");
    } else {
      await dispatch(setIsTechniqueLoading(true));
      navigate(`/techniques?id=${technique?.id}`);
    }
  };

  const resetFilters = () => {
    setFilters({
      contexts: [],
      types: [],
      techObjectives: [],
      duration: {
        $gte: minDuration,
        $lte: maxDuration,
      },
      participants: "",
      modalities: [],
      level: [],
    });
  };

  const handleCardFlip = (id: number) => {
    if (flippedCards.includes(id)) {
      setFlippedCards(flippedCards.filter((cardId) => cardId !== id));
    } else {
      setFlippedCards([...flippedCards, id]);
    }
  };

  return (
    <>
      <div
        className={`flex flex-row overflow-x-hidden ${
          isFiltersOpen ? "filters-open" : "filters-closed"
        } ${isAIFeedbackToastOpen ? "pb-[200px]" : ""}`}
        style={{ width: isDesktop ? "100%" : "200vw" }}
      >
        <div className="w-screen px-2 pt-2 md:px-12 md:pt-8">
          <PageHeader
            search={search}
            setSearch={setSearch}
            title={translate("ns1:Routes.Techniques")}
            placeholder="TechniqueName"
            subTitle={translate("ns1:Techniques.Subtitle")}
            activityCount={techniques.length}
            aiSearch
            resetAISearchFilters={resetFilters}
          />

          {isDesktop ? (
            <>
              <DesktopActivitiesFilters
                filters={filters}
                setFilters={setFilters}
                fetchActivities={fetchTechniques}
                activitiesType="techniques"
                minDuration={minDuration}
                maxDuration={maxDuration}
                resetFilters={resetFilters}
              />
              {isSearching && isDesktop && (
                <div className="w-100 mt-8 flex items-center justify-center rounded-[20px] bg-darkGrey-200 px-6 py-4">
                  <HiSparkles size={24} className="me-4 text-darkGrey-700" />
                  <span className="text-base font-medium text-secondaryGrey-800">
                    {translate("ns1:AI.SearchResults")}
                  </span>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="mt-7 flex">
                <div className="me-3 w-full">
                  <INSearchField
                    placeholder={translate(
                      "ns1:Input.Placeholders.TechniqueName"
                    )}
                    value={search}
                    setValue={setSearch}
                    width="100%"
                    height="48px"
                  />
                </div>
                <AISearchTooltip requestFeedback>
                  <AISearchButton onClick={modalAISearch.onOpen} />
                </AISearchTooltip>
              </div>
              <ObjectivesTooltip>
                <button
                  className="mt-3 flex w-full items-center justify-center rounded-xl border border-darkGrey-200 bg-white py-2 text-base font-bold"
                  onClick={() => setIsFiltersOpen(true)}
                >
                  <HiAdjustments color="#2B3674" size="20" className="me-1.5" />
                  {translate("ns1:Button.Filters")}
                  <span className="ms-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-pink-400 text-xs text-white">
                    {getFiltersCount(filters)}
                  </span>
                </button>
              </ObjectivesTooltip>
            </>
          )}

          <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {!isLoading ? (
              <>
                {techniques.map((technique, index) => (
                  <TechniqueCard
                    technique={technique}
                    key={"technique" + index}
                    onClick={() => handleCardClick(technique)}
                    isFlipped={flippedCards.includes(technique.id)}
                    flipCard={() => handleCardFlip(technique.id)}
                  />
                ))}
              </>
            ) : (
              <>
                {[...Array(12)].map((_, index) => (
                  <INSkeleton key={"techniques-skeleton" + index} />
                ))}
              </>
            )}
          </div>
        </div>
        {!isDesktop && (
          <MobileActivitiesFilters
            filters={filters}
            setFilters={setFilters}
            fetchActivities={fetchTechniques}
            activitiesType="techniques"
            minDuration={minDuration}
            maxDuration={maxDuration}
            setIsFiltersOpen={setIsFiltersOpen}
            resetFilters={resetFilters}
          />
        )}

        <ModalAISearch
          isOpen={modalAISearch.isOpen}
          onClose={modalAISearch.onClose}
          requestFeedback
          resetFilters={resetFilters}
        />
      </div>
      <AIFeedbackToast />
    </>
  );
}

const ObjectivesTooltip = ({ children }: PropsWithChildren) => {
  const onBoardingValues = useAppSelector(selectOnBoardingValues);
  const userId = useAppSelector(selectUserId);
  const dispatch = useAppDispatch();

  const closeObjectivesTooltip = () => {
    if (!onBoardingValues.closedObjectiveAdvice) {
      updateOnBoardingValue(userId, "closedObjectiveAdvice", dispatch);
    }
  };

  useEffect(() => {
    window.addEventListener("beforeunload", closeObjectivesTooltip);
    return () => {
      closeObjectivesTooltip();
    };
  }, []); //eslint-disable-line

  // return onBoardingValues.closedAISearchTooltip ? (
  return (
    <CustomTooltip
      open={!onBoardingValues.closedObjectiveAdvice}
      onClose={closeObjectivesTooltip}
      title={translate("ns1:OnBoarding.Tooltips.ObjectiveTitle")}
      text={translate("ns1:OnBoarding.Tooltips.ObjectiveText")}
    >
      {children}
    </CustomTooltip>
  );
  // ) : (
  //   <>{children}</>
  // );
};
